import { ActionCheckCircle } from '../../svg'
import { cn } from '../../utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '../Button'
import { TableHeaderActionsProps } from './Table.types'
import { TableActionDropdown } from './TableActionDropdown'

/**
 * Detects whether inline action buttons would overflow the available space.
 *
 * Renders a hidden full-row measurer (absolute, h-0, overflow-hidden)
 * that mirrors all three grid columns in a single flex-nowrap line.
 * When measurer.scrollWidth > measurer.clientWidth, the buttons don't fit.
 *
 * Stable: the measurer's width comes from the parent container (viewport-driven),
 * not from the center column content, so toggling between buttons/dropdown
 * doesn't change the measurement.
 */
const useActionsOverflow = () => {
    const containerRef = useRef<HTMLDivElement>(null)
    const measurerRef = useRef<HTMLDivElement>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)

    const checkOverflow = useCallback(() => {
        const measurer = measurerRef.current
        if (!measurer) return
        setIsOverflowing(measurer.scrollWidth > measurer.clientWidth)
    }, [])

    useEffect(() => {
        const container = containerRef.current
        const measurer = measurerRef.current
        if (!container) return

        checkOverflow()

        const observer = new ResizeObserver(checkOverflow)
        observer.observe(container)
        if (measurer) observer.observe(measurer)
        return () => observer.disconnect()
    }, [checkOverflow])

    return { containerRef, measurerRef, isOverflowing }
}

export const TableHeaderActions = ({
    tableLeftComponent,
    isDetails,
    tableRightComponent,
    tableActions,
    checkboxState,
    setIsClicked,
    setIsAllSelected,
    handleSelectAll,
    isAllSelected,
    markAllButtonLabel,
    isMultiRowSelect,
    tableData,
    isSelected = true,
    t,
}: TableHeaderActionsProps) => {
    const isSelectedItem = Object.keys(checkboxState).filter(
        (key) => checkboxState[key]
    ).length

    const showActions = !!(tableActions && isSelectedItem)
    const { containerRef, measurerRef, isOverflowing } = useActionsOverflow()
    const selectAllButton =
        isMultiRowSelect && tableData?.length ? (
            <Button
                label={
                    markAllButtonLabel ||
                    t('button-actions.select-all') ||
                    'Označi sve'
                }
                className={cn(
                    'whitespace-nowrap stroke-background-base sm:!rounded-l-lg',
                    // Desktop: round the right side only when no real
                    // (non-empty) sibling follows — keeps the select-all
                    // button a full pill when permissions hide every table
                    // action, and lets it join the group when one renders.
                    // Mobile corners come from the toolbar (see below).
                    'sm:[&:not(:has(~_*:not(:empty)))]:!rounded-r-lg'
                )}
                iconBefore={<ActionCheckCircle />}
                color="group"
                shape="group"
                variant="group"
                size="sm"
                onClick={() => {
                    setIsClicked(true)
                    setIsAllSelected(!isAllSelected)
                    handleSelectAll()
                }}
            />
        ) : null

    // Mobile splits the toolbar into two groups. All four controls in one
    // row squeeze the search down to a few pixels, so when both groups exist
    // they stack: filter + search on top, selection + actions below.
    const hasMobileSearchGroup = !!(tableLeftComponent || tableRightComponent)
    const hasMobileSelectionGroup =
        !isDetails && isSelected && !!(selectAllButton || showActions)

    // The pill is the only chrome: buttons and inputs inside drop their own
    // shadow and take the concentric radius (pill radius minus its p-1),
    // whatever their desktop rounding.
    const mobilePillClasses = cn(
        'flex items-center gap-1 rounded-xl bg-background-base p-1 shadow-group',
        '[&_button]:!rounded-[calc(var(--radius-xl)-0.25rem)] [&_button]:!shadow-none',
        '[&_input]:!rounded-[calc(var(--radius-xl)-0.25rem)] [&_input]:!shadow-none'
    )

    return (
        <div
            ref={containerRef}
            className="relative w-full rounded-b-md bg-background-base py-1 transition-shadow duration-200"
            style={{
                position: 'sticky',
                top: 'calc(var(--sticky-offset, 0px) + var(--tab-height, 0px))',
                zIndex: 10,
                boxShadow: 'var(--sticky-shadow, none)',
            }}
        >
            {/* Hidden full-row measurer: mirrors all 3 grid columns in
                a single flex-nowrap line. Uses inset-x-0 so its width equals
                the parent (viewport-driven), NOT the center column content.
                scrollWidth > clientWidth means the buttons don't fit. */}
            {showActions && (
                <div
                    ref={measurerRef}
                    className="pointer-events-none invisible absolute inset-x-0 top-0 flex h-0 flex-nowrap items-center gap-4 overflow-hidden px-2"
                    aria-hidden
                >
                    <span className="shrink-0">{tableLeftComponent}</span>
                    <span className="flex shrink-0 items-center whitespace-nowrap">
                        {selectAllButton}
                        {tableActions}
                    </span>
                    <span className="w-60 shrink-0" />
                </div>
            )}

            {/* Mobile layout. Each group is its own pill; a group with no
                search to fill the row hugs its buttons, centred like on
                desktop. Nothing renders when neither group has content. */}
            {(hasMobileSearchGroup || hasMobileSelectionGroup) && (
                <div className="flex flex-col gap-2 px-2 sm:hidden">
                    {hasMobileSearchGroup && (
                        <div
                            className={cn(
                                mobilePillClasses,
                                tableRightComponent ? 'w-full' : 'mx-auto w-fit'
                            )}
                        >
                            {tableLeftComponent && (
                                <div className="shrink-0">
                                    {tableLeftComponent}
                                </div>
                            )}
                            {tableRightComponent && (
                                <div className="min-w-0 flex-1">
                                    {tableRightComponent}
                                </div>
                            )}
                        </div>
                    )}
                    {hasMobileSelectionGroup && (
                        <div className={cn(mobilePillClasses, 'mx-auto w-fit')}>
                            {selectAllButton}
                            {showActions ? (
                                <TableActionDropdown
                                    tableActions={tableActions}
                                    className="rounded-r-lg"
                                />
                            ) : null}
                        </div>
                    )}
                </div>
            )}

            {/* Desktop layout: 3-column grid with stable positions */}
            <div className="hidden px-2 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-4">
                <div className="flex items-center">
                    {tableLeftComponent && tableLeftComponent}
                </div>
                <div className="flex items-center justify-center">
                    {!isDetails && isSelected && (
                        <div className="flex items-center justify-center">
                            {selectAllButton}
                            {showActions &&
                                (isOverflowing ? (
                                    <TableActionDropdown
                                        tableActions={tableActions}
                                        className="rounded-r-lg"
                                    />
                                ) : (
                                    <span className="flex whitespace-nowrap">
                                        {tableActions}
                                    </span>
                                ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end">
                    <div className="w-full max-w-60">
                        {tableRightComponent && tableRightComponent}
                    </div>
                </div>
            </div>
        </div>
    )
}
